import { useState } from 'react';
import { App, Button, Flex, Input, Space, Typography } from 'antd';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { LinesGrid } from '@/shared/ui/LinesGrid';
import type { LinesGridColumn } from '@/shared/ui/LinesGrid';
import { IntInput } from '@/shared/ui/inputs';
import { useCreateCategoryMutation, useListCategoriesQuery } from '@/entities/category';
import { translateApiError } from '@/shared/api/errorToMessage';

export interface CategoryCreateWindowProps {
  /** Все строки таблицы уходят в одну и ту же корневую группу — выбора группы построчно нет. */
  parentId: string;
  onClose: () => void;
}

interface Row {
  key: string;
  name: string;
  pcsPerBox: number | null;
}

let keySeq = 0;
function emptyRow(): Row {
  keySeq += 1;
  return { key: `cat${keySeq}-${Date.now()}`, name: '', pcsPerBox: null };
}

/** §2.1: добавление основных категорий одной группы — плавающее окно с табличной формой (тот же
 *  приём, что у `CounterpartyCreateWindow`/документов): несколько строк за один заход, строка
 *  снизу добавляется автоматически по заполнению «Название», пустые/неполные строки не сохраняются. */
export function CategoryCreateWindow({ parentId, onClose }: CategoryCreateWindowProps) {
  const { message } = App.useApp();
  const { data: categories = [] } = useListCategoriesQuery({ includeArchived: true });
  const root = categories.find((c) => c.id === parentId);
  const [rows, setRows] = useState<Row[]>([emptyRow()]);
  const [createCategory, { isLoading }] = useCreateCategoryMutation();

  function addRow() {
    setRows((prev) => [...prev, emptyRow()]);
  }

  function removeRow(key: string) {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
  }

  function updateName(key: string, rowIndex: number, value: string) {
    setRows((prev) => {
      const wasEmpty = !prev[rowIndex]?.name;
      const next = prev.map((r) => (r.key === key ? { ...r, name: value } : r));
      if (rowIndex === prev.length - 1 && wasEmpty && value.trim()) {
        return [...next, emptyRow()];
      }
      return next;
    });
  }

  function updatePcsPerBox(key: string, value: number | null) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, pcsPerBox: value } : r)));
  }

  const filled = rows.filter((r) => r.name.trim() && r.pcsPerBox != null);
  // Строка с названием, но без верного N: сохранять остальные и молча пропускать её нельзя —
  // владелец решит, что товар добавлен. N после первого проведённого документа уже не исправить.
  const incomplete = rows.filter((r) => r.name.trim() && r.pcsPerBox == null);

  async function handleSave() {
    if (filled.length === 0 || incomplete.length > 0) return;
    try {
      await Promise.all(
        filled.map((r) => createCategory({ name: r.name.trim(), parentId, pcsPerBox: r.pcsPerBox ?? undefined }).unwrap()),
      );
      message.success(filled.length > 1 ? `Добавлено категорий: ${filled.length}` : 'Категория добавлена');
      onClose();
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  const columns: LinesGridColumn<Row>[] = [
    {
      key: 'name',
      title: 'Название',
      width: 260,
      render: (row, rowIndex) => (
        <Input
          className="nav-cell"
          data-nav-row={rowIndex}
          data-nav-col={0}
          value={row.name}
          autoFocus={rowIndex === 0}
          onChange={(e) => updateName(row.key, rowIndex, e.target.value)}
        />
      ),
    },
    {
      key: 'pcsPerBox',
      title: 'Штук в коробке (N)',
      width: 140,
      align: 'right',
      render: (row, rowIndex) => (
        <IntInput value={row.pcsPerBox} onChange={(v) => updatePcsPerBox(row.key, v)} navRow={rowIndex} navCol={1} />
      ),
    },
  ];

  return (
    <FloatingWindow
      kind="categoryCreate"
      title={`Новые категории: ${root?.name ?? ''}`}
      onClose={onClose}
      footer={
        <Space>
          <Button type="primary" loading={isLoading} disabled={filled.length === 0 || incomplete.length > 0} onClick={() => void handleSave()}>
            Сохранить{filled.length > 1 ? ` (${filled.length})` : ''}
          </Button>
          <Button onClick={onClose}>Закрыть</Button>
        </Space>
      }
    >
      <Flex vertical gap={8}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Заполните название и N — строка снизу добавится автоматически. Пустые строки не сохраняются.
        </Typography.Text>
        {incomplete.length > 0 ? (
          <Typography.Text type="danger" style={{ fontSize: 12 }}>
            Укажите «Штук в коробке» — целое число от 1 — у каждой строки с названием: {incomplete.map((r) => `«${r.name.trim()}»`).join(', ')}
          </Typography.Text>
        ) : null}
        <LinesGrid lines={rows} columns={columns} getRowKey={(r) => r.key} onAddRow={addRow} onRemoveRow={removeRow} />
      </Flex>
    </FloatingWindow>
  );
}
